import React, { useState } from 'react';
import Dialog from './Dialog';
import Button from './Button';
import useStore from '@store';
import { Copy, Check, Share2, Mail, MessageCircle, Link, QrCode } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ShareDialog() {
  const { showShareDialog, closeShareDialog, isProtected, password } = useStore();
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [copiedBoth, setCopiedBoth] = useState(false);
  
  const url = window.location.href;
  
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };
  
  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopiedPassword(true);
      toast.success('Password copied to clipboard!');
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };
  
  const copyBoth = async () => {
    const text = `List: ${url}\nPassword: ${password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedBoth(true);
      toast.success('Link and password copied!');
      setTimeout(() => setCopiedBoth(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };
  
  const shareViaEmail = () => {
    const subject = 'Check out this list on LetsNote';
    const body = isProtected && password 
      ? `I've shared a list with you on LetsNote:\n\nLink: ${url}\nPassword: ${password}\n\nYou can view and edit this list in real-time!`
      : `I've shared a list with you on LetsNote:\n\n${url}\n\nYou can view and edit this list in real-time!`;
    
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };
  
  const shareViaWhatsApp = () => {
    const text = isProtected && password
      ? `Check out this list on LetsNote: ${url}\n\nPassword: ${password}`
      : `Check out this list on LetsNote: ${url}`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
  };
  
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'LetsNote List',
          text: isProtected ? `Protected list (password: ${password})` : 'Collaborative list',
          url: url,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    }
  };
  
  return (
    <Dialog
      isOpen={showShareDialog}
      onClose={closeShareDialog}
      title="Share This List"
      description={
        isProtected 
          ? "Share both the link and password with people you trust to edit"
          : "Anyone with this link can view and edit this list"
      }
      maxWidth="max-w-lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Link Section */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Link style={{ width: '16px', height: '16px', color: '#3b82f6' }} />
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
              List Link
            </label>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <input
              type="text"
              value={url}
              readOnly
              style={{
                flex: 1,
                padding: '12px',
                fontSize: '14px',
                backgroundColor: '#f9fafb',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                fontFamily: 'inherit',
                color: '#374151'
              }}
              onClick={(e) => e.target.select()}
            />
            <button
              onClick={copyUrl}
              style={{
                padding: '12px 16px',
                backgroundColor: copiedUrl ? '#f0fdf4' : '#f3f4f6',
                border: `1px solid ${copiedUrl ? '#bbf7d0' : '#d1d5db'}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: copiedUrl ? '#166534' : '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              {copiedUrl ? <Check style={{ width: '16px', height: '16px' }} /> : <Copy style={{ width: '16px', height: '16px' }} />}
              {copiedUrl ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
        
        {/* Password Section (if protected) */}
        {isProtected && password && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <div style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '10px', color: '#d97706', fontWeight: 'bold' }}>P</span>
              </div>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                Password
              </label>
              <span style={{
                fontSize: '12px',
                color: '#d97706',
                backgroundColor: '#fef3c7',
                padding: '2px 8px',
                borderRadius: '12px'
              }}>
                Required to edit
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                value={password}
                readOnly
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '14px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  outline: 'none',
                  fontFamily: 'monospace',
                  color: '#92400e'
                }}
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={copyPassword}
                style={{
                  padding: '12px 16px',
                  backgroundColor: copiedPassword ? '#f0fdf4' : '#f3f4f6',
                  border: `1px solid ${copiedPassword ? '#bbf7d0' : '#d1d5db'}`,
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: copiedPassword ? '#166534' : '#374151',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {copiedPassword ? <Check style={{ width: '16px', height: '16px' }} /> : <Copy style={{ width: '16px', height: '16px' }} />}
                {copiedPassword ? 'Copied!' : 'Copy'}
              </button>
            </div>
            
            <button
              onClick={copyBoth}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: copiedBoth ? '#f0fdf4' : '#3b82f6',
                border: `1px solid ${copiedBoth ? '#bbf7d0' : '#3b82f6'}`,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: copiedBoth ? '#166534' : 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              {copiedBoth ? <Check style={{ width: '16px', height: '16px' }} /> : <Copy style={{ width: '16px', height: '16px' }} />}
              {copiedBoth ? 'Both Copied!' : 'Copy Link & Password'}
            </button>
          </div>
        )}
        
        {/* Quick Share */}
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937', marginBottom: '16px' }}>
            Quick Share
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <button
              onClick={shareViaEmail}
              style={{
                padding: '12px 16px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <Mail style={{ width: '16px', height: '16px' }} />
              Email
            </button>
            
            <button
              onClick={shareViaWhatsApp}
              style={{
                padding: '12px 16px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <MessageCircle style={{ width: '16px', height: '16px' }} />
              WhatsApp
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}